/**
 * Mock auth path — used when VITE_MOCK_AUTH !== 'false'.
 *
 * Returns the fixture idToken that the backend's MockFirebaseAuthVerifier
 * recognises ("fixture-token-default" maps to fixture-uid-default).
 *
 * This module is the ONLY thing the auth code touches in mock mode — the
 * real Firebase Web SDK is never imported.
 *
 * Session persistence (mock-only): to mirror Firebase's cross-reload session
 * persistence in dev, the fixture session is stored in sessionStorage so a
 * page refresh restores it instead of bouncing to /login (comment #8). This is
 * SAFE because the fixture token is NOT a real credential — the backend accepts
 * it only when MOCK_PROVIDERS=true. Real Firebase mode never touches this store;
 * it relies on the SDK's own (IndexedDB-backed) persistence.
 */
import type { AuthSession } from './types';

export const FIXTURE_TOKEN = 'fixture-token-default';

export const FIXTURE_USER = {
  uid: 'fixture-uid-default',
  email: 'user@example.com',
  displayName: 'Default User',
} as const;

const MOCK_SESSION_KEY = 'ballwatcher.mockAuth.session';

function mockStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // intentional swallow: sessionStorage can be unavailable (sandboxed iframe,
    // privacy mode). Mock persistence is a dev convenience — fall back to
    // in-memory only (session simply won't survive a refresh).
    return null;
  }
}

function persistMockSession(session: AuthSession): void {
  try {
    mockStore()?.setItem(MOCK_SESSION_KEY, JSON.stringify(session));
  } catch {
    // intentional swallow: writes can throw if storage is full/disabled;
    // persistence is best-effort for a dev-only fixture session.
  }
}

/**
 * Synchronously restore a previously-persisted fixture session, or null.
 *
 * Only the exact known fixture shape is accepted; any other / corrupt value is
 * ignored so a stale or tampered store can never inject a bogus session.
 */
export function mockRestoreSession(): AuthSession | null {
  try {
    const raw = mockStore()?.getItem(MOCK_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthSession>;
    if (parsed?.idToken === FIXTURE_TOKEN && parsed?.user?.uid === FIXTURE_USER.uid) {
      return { user: { ...FIXTURE_USER }, idToken: FIXTURE_TOKEN };
    }
    return null;
  } catch {
    // intentional swallow: getItem/JSON.parse can throw (disabled storage,
    // corrupt value). Treat anything unreadable as "no persisted session".
    return null;
  }
}

export async function mockSignInWithGoogle(): Promise<AuthSession> {
  await new Promise((r) => setTimeout(r, 20)); // mimic real-SDK async-ness
  const session: AuthSession = {
    user: { ...FIXTURE_USER },
    idToken: FIXTURE_TOKEN,
  };
  persistMockSession(session);
  return session;
}

export async function mockGetIdToken(): Promise<string> {
  return FIXTURE_TOKEN;
}

export async function mockSignOut(): Promise<void> {
  try {
    mockStore()?.removeItem(MOCK_SESSION_KEY);
  } catch {
    // intentional swallow: nothing to clear if storage is unavailable.
  }
}
